import { useState } from 'react'
import type { ClientProfile } from '@shared/types'

const AVAILABLE_FUNCTIONS = [
  { key: 'HR', label: 'Human Resources', icon: '👥', desc: 'Talent, learning, HR ops, workforce planning' },
  { key: 'Finance', label: 'Finance', icon: '💰', desc: 'FP&A, accounting, treasury, tax, audit' },
  { key: 'Supply Chain', label: 'Supply Chain', icon: '🔗', desc: 'Procurement, logistics, planning, quality' },
  { key: 'Technology', label: 'Technology / IT', icon: '💻', desc: 'Engineering, infra, cyber, data, product' },
  { key: 'Marketing', label: 'Marketing', icon: '📣', desc: 'Brand, digital, content, analytics, campaigns' },
  { key: 'Customer Service', label: 'Customer Service', icon: '🎧', desc: 'Contact center, CX design, case management' },
  { key: 'Legal', label: 'Legal', icon: '⚖️', desc: 'Contracts, compliance, IP, privacy, governance' },
  { key: 'Operations', label: 'Operations', icon: '⚙️', desc: 'Process excellence, facilities, risk, shared services' },
]

const AMBITION_LABELS: Record<number, string> = {
  1: 'Conservative — focus on Human+Machine augmentation, minimal AI autonomy',
  2: 'Moderate-Conservative — cautious automation, proven use cases only',
  3: 'Balanced — realistic mix of all five modes based on task characteristics',
  4: 'Moderate-Aggressive — lean into automation where credible',
  5: 'Aggressive — maximize AI potential across all eligible tasks',
}

interface Props {
  profile: Partial<ClientProfile>
  onProfileChange: (p: Partial<ClientProfile>) => void
  onBack: () => void
  onNext: () => void
}

export default function Step2_FunctionScope({ profile, onProfileChange, onBack, onNext }: Props) {
  // enterpriseMode = user explicitly selected "Full Enterprise" (functionScope = [])
  // Separate from "nothing selected yet" which also gives functionScope = []
  const [enterpriseMode, setEnterpriseMode] = useState(true)

  const selected = profile.functionScope || []
  const ambition = profile.ambitionLevel || 3

  const toggleFunction = (key: string) => {
    const next = selected.includes(key)
      ? selected.filter((s) => s !== key)
      : [...selected, key]
    if (next.length === AVAILABLE_FUNCTIONS.length) {
      setEnterpriseMode(true)
      onProfileChange({ ...profile, functionScope: [] })
    } else {
      setEnterpriseMode(false)
      onProfileChange({ ...profile, functionScope: next })
    }
  }

  const toggleEnterprise = () => {
    if (enterpriseMode) {
      // Deselect enterprise — go back to empty selection
      setEnterpriseMode(false)
      onProfileChange({ ...profile, functionScope: [] })
    } else {
      // Select full enterprise — empty array signals AI to identify ALL functions
      setEnterpriseMode(true)
      onProfileChange({ ...profile, functionScope: [] })
    }
  }

  const selectedCount = selected.length

  return (
    <div className="max-w-2xl">
      <h2 className="text-xl font-semibold text-gray-900 mb-1">Function Scope</h2>
      <p className="text-sm text-gray-500 mb-5">
        Select which business functions to include. Claude will research your client and generate subfunctions specific to their industry and scale.
      </p>

      {/* Full Enterprise toggle */}
      <button
        onClick={toggleEnterprise}
        className={`w-full mb-4 p-4 rounded-xl border-2 transition-all text-left ${
          enterpriseMode
            ? 'border-purple-500 bg-purple-50'
            : 'border-dashed border-gray-300 bg-white hover:border-purple-300 hover:bg-purple-50/40'
        }`}
      >
        <div className="flex items-center gap-3">
          <span className="text-2xl">🏢</span>
          <div className="flex-1">
            <div className={`font-bold text-sm ${enterpriseMode ? 'text-purple-800' : 'text-gray-800'}`}>
              Full Enterprise / Entire Organization
            </div>
            <div className="text-xs text-gray-400">AI identifies ALL business functions relevant to this company — not limited to a predefined list</div>
          </div>
          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
            enterpriseMode ? 'border-purple-500 bg-purple-500' : 'border-gray-300'
          }`}>
            {enterpriseMode && <span className="text-white text-xs">✓</span>}
          </div>
        </div>
      </button>

      <div className="relative mb-5">
        <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
        <div className="relative flex justify-center text-xs text-gray-400 uppercase tracking-widest">
          <span className="bg-white px-3">or select individual functions</span>
        </div>
      </div>

      {/* Function checkboxes */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        {AVAILABLE_FUNCTIONS.map((fn) => {
          const isSelected = selected.includes(fn.key)
          return (
            <button
              key={fn.key}
              onClick={() => toggleFunction(fn.key)}
              className={`text-left p-4 rounded-xl border-2 transition-all ${
                isSelected
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-200 bg-white hover:border-gray-300'
              }`}
            >
              <div className="flex items-center gap-3">
                <span className="text-xl">{fn.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className={`font-semibold text-sm ${isSelected ? 'text-purple-800' : 'text-gray-800'}`}>
                    {fn.label}
                  </div>
                  <div className="text-xs text-gray-400 truncate">{fn.desc}</div>
                </div>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                  isSelected ? 'border-purple-500 bg-purple-500' : 'border-gray-300'
                }`}>
                  {isSelected && <span className="text-white text-xs">✓</span>}
                </div>
              </div>
            </button>
          )
        })}
      </div>

      {(selectedCount > 0 || enterpriseMode) && (
        <div className="mb-6 p-3 bg-purple-50 rounded-lg text-sm text-purple-700">
          {enterpriseMode ? (
            <>
              <strong>Full Enterprise</strong> — Claude will research {profile.companyName || 'your client'} and identify{' '}
              <strong>every business function</strong> with meaningful headcount, including functions beyond HR, Finance, and the other defaults
            </>
          ) : (
            <>
              <strong>{selectedCount} function{selectedCount > 1 ? 's' : ''}</strong> selected — Claude will research{' '}
              {profile.companyName || 'your client'} and generate <strong>client-specific subfunctions</strong> per function
            </>
          )}
        </div>
      )}

      {/* Ambition slider */}
      <div className="mb-8">
        <label className="block text-sm font-semibold text-gray-700 mb-3">
          AI Transformation Ambition Level: <span className="text-purple-600">{ambition}/5</span>
        </label>
        <input
          type="range"
          min={1}
          max={5}
          value={ambition}
          onChange={(e) => onProfileChange({ ...profile, ambitionLevel: Number(e.target.value) })}
          className="w-full accent-purple-600"
        />
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          <span>Conservative</span>
          <span>Balanced</span>
          <span>Aggressive</span>
        </div>
        <p className="text-xs text-gray-500 mt-2 bg-gray-50 p-2 rounded-lg">{AMBITION_LABELS[ambition]}</p>
      </div>

      <div className="flex gap-3">
        <button
          onClick={onBack}
          className="py-3 px-6 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:border-gray-300 transition-all"
        >
          ← Back
        </button>
        <button
          onClick={onNext}
          disabled={!enterpriseMode && selected.length === 0}
          className="py-3 px-8 rounded-xl text-white text-sm font-semibold disabled:opacity-40 transition-all hover:scale-105"
          style={{ background: 'linear-gradient(135deg, #A100FF, #7900B8)' }}
        >
          Generate Navigator →
        </button>
      </div>
    </div>
  )
}
